import React, {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'
import {makeStyles} from '@material-ui/core/styles'
import DatePicker from 'react-datepicker'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

import useFailureModal from 'features/modal/useFailureModal'
import useInvoiceView from 'features/invoice/useInvoiceView'

import CardWithTitle from 'components/atoms/CardWithTitle'
import NoticeConfirmDialog from 'components/Dialog/NoticeConfirmDialog'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import UsageSummary from 'pages/CustomerManagement/InvoiceView/Elements/UsageSummary'
import TransactionStatement from 'pages/CustomerManagement/InvoiceView/Elements/TransactionStatement'
import AppLoading from 'components/atoms/Loading'
import {Locale} from 'components/atoms/TranslationSelect'

import UsageDetail from 'pages/CustomerManagement/InvoiceView/Elements/UsageDetail'
import InvoiceViewStyle from 'pages/CustomerManagement/InvoiceView/Style'
import RouteConstant from 'constants/RouteConstant'

import {dateToDashMonthString, getMonthYear} from 'helpers/dateHelper'
import useSuccessModal from 'features/modal/useSuccessModal'
import 'react-datepicker/dist/react-datepicker.css'
import useProductManagement from 'features/productManagement/useProductManagement'

const useStyles = makeStyles(InvoiceViewStyle)

function InvoiceView() {
  const {t, i18n} = useTranslation()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const history = useHistory()
  const classes = useStyles()
  const dispatch = useDispatch()
  const {
    invoiceInformation,
    error,
    getInvoice,
    resetInvoiceDataAction,
    setError,
    loading,
    onSaveInvoice,
  } = useInvoiceView()
  const {id: invoiceId} = invoiceInformation || {}
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {getAllPackageList} = useProductManagement()
  const {usageSummaryTable} = useInvoiceView()

  const [orgId, setOrgId] = useState<string>('')
  const [monthPickerValue, setMonthPickerValue] = useState<Date>()
  const [usageMonth, setUsageMonth] = useState<string>(
    queryParams.get('usageMonth') || '',
  )
  const [totalPriceAmount, setTotalPriceAmount] = useState<number>(0)
  const [listCheckedItemId, setListCheckedItemId] = useState<string[]>([])

  const onChangeMonthPicker = async (dateInput: Date | null) => {
    if (!dateInput) return
    setMonthPickerValue(dateInput)
    resetInvoiceDataAction()
    getInvoice(orgId, dateToDashMonthString(dateInput))
    history.replace(
      `${
        RouteConstant.INVOICE_VIEW.path
      }?orgId=${orgId}&usageMonth=${dateToDashMonthString(dateInput)}`,
    )
  }

  const componentRef = useRef<HTMLDivElement>(null)

  const cropImage = (
    imgData: string,
    startX: number,
    startY: number,
    width: number,
    height: number,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.src = imgData
      image.onload = () => {
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        if (!tempCtx) {
          reject(new Error('Could not get canvas context'))
          return
        }
        tempCanvas.width = width
        tempCanvas.height = height
        tempCtx.drawImage(
          image,
          startX,
          startY,
          width,
          height,
          0,
          0,
          width,
          height,
        )
        const croppedImageData = tempCanvas.toDataURL('image/jpeg')
        resolve(croppedImageData)
      }
      image.onerror = () => {
        reject(new Error('Failed to load image'))
      }
    })
  }

  const HEIGHT_A4_PX = 1836
  const WIDTH_CONTENT_PX = 1280
  const HEIGHT_A4_MM = 287
  const WIDTH_A4_MM = 200
  const MARGIN_CONTENT_MM = 5
  const SCALE = 3

  const generatePDFImage = async (
    index: number,
    scale: number,
    imgData: string,
    pdf: JsPDF,
    heightPx: number,
  ) => {
    if (index > 0) {
      pdf.addPage()
    }
    await cropImage(
      imgData,
      0,
      index * HEIGHT_A4_PX * scale,
      WIDTH_CONTENT_PX * scale,
      (heightPx > HEIGHT_A4_PX ? HEIGHT_A4_PX : heightPx) * scale,
    )
      .then(async (croppedImage1) => {
        pdf.addImage(
          croppedImage1,
          'JPEG',
          MARGIN_CONTENT_MM,
          MARGIN_CONTENT_MM,
          WIDTH_A4_MM,
          heightPx > HEIGHT_A4_PX
            ? HEIGHT_A4_MM
            : (HEIGHT_A4_MM * heightPx) / HEIGHT_A4_PX,
        )
        heightPx -= heightPx > HEIGHT_A4_PX ? HEIGHT_A4_PX : heightPx
        if (heightPx > 0) {
          pdf = await generatePDFImage(index + 1, scale, imgData, pdf, heightPx)
        }
      })
      .catch((error) => {
        console.error(`Error cropping image ${index}:`, error)
      })
    return pdf
  }

  const handleSaveInvoice = async () => {
    const input = componentRef.current
    if (!input) return

    const canvas = await html2canvas(input, {
      scale: SCALE,
      width: WIDTH_CONTENT_PX,
      height: input.scrollHeight,
    })
    const imgData = canvas.toDataURL('image/jpeg')

    const pdf = new JsPDF('portrait', 'mm', 'a4')
    const result = await generatePDFImage(
      0,
      SCALE,
      imgData,
      pdf,
      input.scrollHeight,
    )
    const pdfBlob = result.output('blob')
    await onSaveInvoice(pdfBlob)
    const file = new File([pdfBlob], 'invoice.pdf', {type: 'application/pdf'})

    // Download the generated PDF
    const downloadLink = document.createElement('a')
    downloadLink.href = URL.createObjectURL(file)
    downloadLink.download = `invoice_${usageMonth}.pdf`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const handleCheckItem = (usageId: string, price: number) => {
    if (listCheckedItemId.includes(usageId)) {
      setListCheckedItemId((prev) =>
        prev.filter((itemId) => itemId !== usageId),
      )
      setTotalPriceAmount((prev) => prev - price)
    } else {
      setListCheckedItemId((prev) => [...prev, usageId])
      setTotalPriceAmount((prev) => prev + price)
    }
  }

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allIds = usageSummaryTable.reduce<string[]>((acc, item) => {
        item.usageLogs.forEach(({usageId}) => {
          acc.push(usageId)
        })
        return acc
      }, [])
      setListCheckedItemId(allIds)
      const totalAmount = usageSummaryTable.reduce((acc, item) => {
        item.usageLogs.forEach(({usageQuantity, packagePrice}) => {
          acc += packagePrice * usageQuantity
        })
        return acc
      }, 0)
      setTotalPriceAmount(totalAmount)
    } else {
      setListCheckedItemId([])
      setTotalPriceAmount(0)
    }
  }

  useEffect(() => {
    const initUsageMonth = queryParams.get('billingMonth')
    const initOrgId = queryParams.get('orgId')
    if (initOrgId) setOrgId(initOrgId)
    if (initUsageMonth) setUsageMonth(initUsageMonth)
    getAllPackageList()
  }, [])

  useEffect(() => {
    if (!orgId || !usageMonth) return
    setMonthPickerValue(new Date(usageMonth))
    getInvoice(orgId, usageMonth)
  }, [usageMonth, orgId])

  useEffect(() => {
    if (!error) return
    onFailureModalOpen(error?.message || error || t('IProcessFail')).then(() =>
      dispatch(setError(null)),
    )
  }, [error])

  useEffect(() => {
    const {totalAmount, listCheckedId} = usageSummaryTable.reduce<{
      totalAmount: number
      listCheckedId: string[]
    }>(
      (acc, item) => {
        item.usageLogs.forEach(({usageQuantity, packagePrice, usageId}) => {
          acc.totalAmount += packagePrice * usageQuantity
          acc.listCheckedId.push(usageId)
        })
        return acc
      },
      {totalAmount: 0, listCheckedId: []},
    )
    setTotalPriceAmount(totalAmount)
    setListCheckedItemId(listCheckedId)
  }, [usageSummaryTable])

  return (
    <>
      <AppLoading loading={loading || false} />
      {loading && <div>loading</div>}
      <div className={classes.topRoot}>
        <div className={classes.container}>
          <CardWithTitle
            title={t('IBillingDetails')}
            className={classes.invoiceView}
          >
            <div className={classes.InvoiceSettingContainer}>
              <div className={classes.invoiceAction}>
                <div style={{width: 150}}>
                  <DatePicker
                    selected={monthPickerValue}
                    onChange={onChangeMonthPicker}
                    dateFormat='yyyy-MM'
                    showMonthYearPicker
                    maxDate={new Date()}
                  />
                </div>
                <ActionButton
                  variant='contained'
                  disableElevation
                  color='primary'
                  onClick={handleSaveInvoice}
                >
                  {invoiceId ? t('IUpdate') : t('ICreateInvoice')}
                </ActionButton>
              </div>
              <div className={classes.invoiceViewInfo} ref={componentRef}>
                <p className={classes.titleInvoice}>
                  <span className={classes.titleMonthInvoice}>{`${getMonthYear(
                    monthPickerValue || new Date(),
                    i18n.language as Locale,
                  )}`}</span>
                  {`  ${t('ITitleInvoice')}`}
                </p>
                <TransactionStatement totalAmount={totalPriceAmount} />
                <UsageSummary totalAmount={totalPriceAmount} />
                <UsageDetail
                  listCheckedId={listCheckedItemId}
                  onCheckItem={handleCheckItem}
                  onCheckAll={handleCheckAll}
                />
              </div>
            </div>
          </CardWithTitle>
        </div>
      </div>
      <NoticeConfirmDialog />
    </>
  )
}

export default InvoiceView
